import { useEffect, useState } from "react";
import axios from "axios";
import { ROOMS, ROOM_SPECS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ALL_ROOMS = ROOMS.map((r) => r.slug);
export const ROOM_CAPS = Object.fromEntries(ALL_ROOMS.map((s) => [s, ROOM_SPECS[s].maxGuests]));
export const TOTAL_CAP = ALL_ROOMS.reduce((n, s) => n + ROOM_CAPS[s], 0);

const parseLocal = (s) => { const [y, m, d] = String(s).split("-").map(Number); return new Date(y, (m || 1) - 1, d || 1); };
export const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const eachDay = (fromStr, toStr) => {
  const out = [];
  const cur = parseLocal(fromStr), end = parseLocal(toStr);
  while (cur < end) { out.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  return out;
};

const expandBlocked = (ranges) => {
  const s = new Set();
  for (const b of ranges || []) {
    if (!b.arrival || !b.departure) continue;
    for (const d of eachDay(b.arrival, b.departure)) s.add(dayKey(d));
  }
  return s;
};

// Fetch each room's blocked nights (14 months) ONCE. Single source of truth: Smoobu via /api/availability.
export const useRoomAvailability = () => {
  const [blockedByRoom, setBlockedByRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const today = new Date();
    const end = new Date(); end.setMonth(end.getMonth() + 14);
    const fmt = (d) => d.toISOString().split("T")[0];
    setLoading(true);
    Promise.all(
      ALL_ROOMS.map((room) =>
        axios.get(`${API}/availability`, { params: { room, start: fmt(today), end: fmt(end) } })
          .then((r) => expandBlocked(r.data.blocked))
          .catch(() => new Set())
      )
    ).then((sets) => {
      setBlockedByRoom(Object.fromEntries(ALL_ROOMS.map((s, i) => [s, sets[i]])));
    }).finally(() => setLoading(false));
  }, []);
  return { blockedByRoom, loading };
};

// Nights of a stay = [arrival, departure). Check-out day is NOT a night.
export const stayNights = (arrival, departure) => eachDay(arrival, departure).map(dayKey);

const roomFreeAllNights = (blockedByRoom, slug, nights) => {
  const s = blockedByRoom[slug];
  return nights.every((k) => !s.has(k));
};

const kCombinations = (arr, k) => {
  if (k <= 0) return [[]];
  if (k > arr.length) return [];
  const res = [];
  const rec = (start, acc) => {
    if (acc.length === k) { res.push(acc.slice()); return; }
    for (let i = start; i < arr.length; i++) { acc.push(arr[i]); rec(i + 1, acc); acc.pop(); }
  };
  rec(0, []);
  return res;
};

// Can we form EXACTLY roomsWanted rooms (from the free ones) with combined capacity >= guests?
const feasibleFromFree = (freeRooms, guests, roomsWanted) => {
  const rw = Math.max(1, roomsWanted);
  if (rw > freeRooms.length) return false;
  if (rw === 1) return freeRooms.some((s) => ROOM_CAPS[s] >= guests);
  return kCombinations(freeRooms, rw).some((c) => c.reduce((n, s) => n + ROOM_CAPS[s], 0) >= guests);
};

// True if the party can be hosted for the FULL stay with EXACTLY roomsWanted rooms.
export const feasibleForStay = (blockedByRoom, arrival, departure, guests, roomsWanted = 1) => {
  if (!blockedByRoom || !arrival || !departure || departure <= arrival) return true;
  const nights = stayNights(arrival, departure);
  if (nights.length === 0) return true;
  const freeRooms = ALL_ROOMS.filter((slug) => roomFreeAllNights(blockedByRoom, slug, nights));
  return feasibleFromFree(freeRooms, guests, roomsWanted);
};

// Which room-counts (1..min(3,guests)) are feasible for these exact dates — for fallback suggestions.
export const feasibleRoomCounts = (blockedByRoom, arrival, departure, guests) => {
  if (!blockedByRoom || !arrival || !departure || departure <= arrival) return [];
  const out = [];
  for (let rc = 1; rc <= Math.min(3, guests); rc++) {
    if (feasibleForStay(blockedByRoom, arrival, departure, guests, rc)) out.push(rc);
  }
  return out;
};

// Per-night recolor: a night is blocked if you cannot form roomsWanted rooms (cap>=guests) that night.
export const blockedDaysForGuests = (blockedByRoom, guests, roomsWanted = 1) => {
  const out = new Set();
  if (!blockedByRoom) return out;
  const cur = new Date(); cur.setHours(0, 0, 0, 0);
  const end = new Date(); end.setMonth(end.getMonth() + 14);
  while (cur < end) {
    const k = dayKey(cur);
    const freeRooms = ALL_ROOMS.filter((slug) => !blockedByRoom[slug].has(k));
    if (!feasibleFromFree(freeRooms, guests, roomsWanted)) out.add(k);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

const shiftDate = (str, deltaDays) => { const d = parseLocal(str); d.setDate(d.getDate() + deltaDays); return dayKey(d); };

// Find nearby date windows (same duration) that ARE feasible for the party. Client-side, same data.
export const findAlternatives = (blockedByRoom, arrival, departure, guests, { maxDays = 14, limit = 5, roomsWanted = 1 } = {}) => {
  if (!blockedByRoom || !arrival || !departure) return [];
  const nights = stayNights(arrival, departure).length;
  if (nights === 0) return [];
  const todayKey = dayKey(new Date());
  const offsets = [];
  for (let i = 1; i <= maxDays; i++) { offsets.push(-i); offsets.push(i); }
  offsets.sort((a, b) => Math.abs(a) - Math.abs(b) || a - b);
  const results = [];
  for (const off of offsets) {
    const a = shiftDate(arrival, off);
    if (a < todayKey) continue;
    const d = shiftDate(a, nights);
    if (feasibleForStay(blockedByRoom, a, d, guests, roomsWanted)) results.push({ arrival: a, departure: d });
    if (results.length >= limit) break;
  }
  return results;
};
